<template>
  <v-app class="app-background">
    <v-container fluid class="pa-6">
      <!-- Card principal -->
      <v-card elevation="8" class="rounded-xl overflow-hidden">
        <!-- Header del módulo -->
        <v-card-title class="primary white--text py-6 px-8 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon large color="white" class="mr-4">mdi-doctor</v-icon>
            <div>
              <span class="text-h5 font-weight-bold d-block">ABM de Profesionales</span>
              <span class="subtitle-2 white--text opacity-80">Gestión exclusiva para Secretaría y Administración</span>
            </div>
          </div>
          <v-btn
            color="white"
            class="primary--text text-none font-weight-bold rounded-lg px-6"
            elevation="2"
            large
            @click="openCreateDialog"
          >
            <v-icon left>mdi-account-plus</v-icon>
            Nuevo Profesional
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6">
          <!-- Filtros y Búsqueda -->
          <v-row class="mb-4 align-center">
            <v-col cols="12" md="6" lg="5">
              <v-text-field
                v-model="searchLastname"
                label="Buscar por Apellido"
                placeholder="Ingrese apellido del profesional..."
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                @keyup.enter="loadProfesionales"
                @click:clear="onClearSearch"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" lg="3" class="d-flex align-center">
              <v-btn color="primary" class="mr-2" outlined @click="loadProfesionales" :loading="loading">
                <v-icon left>mdi-magnify</v-icon>
                Buscar
              </v-btn>
              <v-btn text @click="resetFilters">
                Limpiar
              </v-btn>
            </v-col>
            <v-col cols="12" md="3" lg="4" class="d-flex justify-end align-center">
              <v-checkbox
                v-model="includeInactive"
                label="Mostrar dados de baja (active=false)"
                hide-details
                dense
                @change="loadProfesionales"
              ></v-checkbox>
            </v-col>
          </v-row>

          <v-divider class="mb-6"></v-divider>

          <!-- Tabla de Profesionales -->
          <v-data-table
            :headers="headers"
            :items="profesionales"
            :loading="loading"
            loading-text="Cargando profesionales..."
            no-data-text="No se encontraron profesionales registrado(s)"
            class="elevation-1 rounded-lg"
            :items-per-page="10"
            :footer-props="{
              'items-per-page-options': [5, 10, 20, 50],
              'items-per-page-text': 'Profesionales por página'
            }"
          >
            <!-- Columna DNI -->
            <template v-slot:item.dni="{ item }">
              <span class="font-weight-medium">{{ getDni(item) }}</span>
            </template>

            <!-- Columna Apellido -->
            <template v-slot:item.lastname="{ item }">
              <span class="font-weight-bold primary--text">{{ getApellido(item) }}</span>
            </template>

            <!-- Columna Nombre -->
            <template v-slot:item.name="{ item }">
              <span>{{ getNombre(item) }}</span>
            </template>

            <!-- Columna Especialidad -->
            <template v-slot:item.especialidad="{ item }">
              <v-chip small color="blue lighten-5" class="blue--text font-weight-medium">
                <v-icon left small color="primary">mdi-stethoscope</v-icon>
                {{ getEspecialidad(item) }}
              </v-chip>
            </template>

            <!-- Columna Teléfono -->
            <template v-slot:item.telefono="{ item }">
              <span>{{ item.telefono || 'Sin teléfono' }}</span>
            </template>

            <!-- Columna Matrícula -->
            <template v-slot:item.matricula="{ item }">
              <span class="grey--text text--dark-2">{{ item.matricula || '-' }}</span>
            </template>

            <!-- Columna Estado -->
            <template v-slot:item.active="{ item }">
              <v-chip
                small
                :color="item.active ? 'success' : 'error'"
                text-color="white"
                class="font-weight-bold"
              >
                <v-icon left small>{{ item.active ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
                {{ item.active ? 'Activo' : 'Dado de baja' }}
              </v-chip>
            </template>

            <!-- Columna Acciones -->
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    class="mr-1"
                    @click="openEditDialog(item)"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Editar profesional</span>
              </v-tooltip>

              <v-tooltip bottom v-if="item.active">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="error"
                    v-bind="attrs"
                    v-on="on"
                    @click="confirmDarDeBaja(item)"
                  >
                    <v-icon>mdi-account-minus</v-icon>
                  </v-btn>
                </template>
                <span>Dar de baja (active=false)</span>
              </v-tooltip>
              <v-tooltip bottom v-else>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="success"
                    v-bind="attrs"
                    v-on="on"
                    @click="reactivar(item)"
                  >
                    <v-icon>mdi-account-check</v-icon>
                  </v-btn>
                </template>
                <span>Reactivar profesional</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Diálogo Crear / Editar Profesional -->
      <v-dialog v-model="createDialog" max-width="650px" persistent>
        <v-card class="rounded-xl">
          <v-card-title class="primary white--text py-4 px-6">
            <v-icon left color="white">{{ isEditing ? 'mdi-account-edit' : 'mdi-account-plus' }}</v-icon>
            {{ isEditing ? 'Editar Profesional' : 'Registrar Nuevo Profesional' }}
          </v-card-title>

          <v-form ref="createForm" v-model="formValid" @submit.prevent="saveNewProfesional">
            <v-card-text class="pa-6">
              <span class="caption grey--text mb-4 d-block">Todos los campos con asterisco (*) son obligatorios</span>

              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newProf.dni"
                    label="*DNI"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'El DNI es requerido']"
                    prepend-inner-icon="mdi-card-account-details"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newProf.matricula"
                    label="Matrícula / Código"
                    outlined
                    dense
                    prepend-inner-icon="mdi-file-certificate"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newProf.name"
                    label="*Nombre"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'El nombre es requerido']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="newProf.lastname"
                    label="*Apellido"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'El apellido es requerido']"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="newProf.gender"
                    :items="genderOptions"
                    item-text="text"
                    item-value="value"
                    label="*Género"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'El género es requerido']"
                  ></v-select>
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="newProf.especialidad"
                    :items="especialidadOptions"
                    item-text="name"
                    item-value="id"
                    label="*Especialidad"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'La especialidad es requerida']"
                  ></v-select>
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="newProf.telefono"
                    label="*Teléfono de contacto"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'El teléfono es requerido']"
                    prepend-inner-icon="mdi-phone"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0 d-flex justify-end">
              <v-btn text color="grey darken-1" @click="closeCreateDialog">
                Cancelar
              </v-btn>
              <v-btn
                color="primary"
                class="px-6 rounded-lg"
                type="submit"
                :loading="saving"
                :disabled="!formValid"
              >
                <v-icon left>mdi-content-save</v-icon>
                {{ isEditing ? 'Actualizar Profesional' : 'Guardar Profesional' }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>

      <!-- Diálogo Confirmar Baja -->
      <v-dialog v-model="deleteDialog" max-width="480px">
        <v-card class="rounded-xl">
          <v-card-title class="error white--text py-4 px-6">
            <v-icon left color="white">mdi-alert-circle</v-icon>
            Confirmar Baja de Profesional
          </v-card-title>
          <v-card-text class="pa-6 text-body-1">
            <p>
              ¿Está seguro que desea dar de baja al profesional
              <strong v-if="selectedProf">{{ getApellido(selectedProf) }}, {{ getNombre(selectedProf) }}</strong>?
            </p>
            <p class="caption grey--text text--dark-1 mb-0">
              El estado pasará a <code>active = false</code>. El profesional no estará visible en la agenda pública de turnos.
            </p>
          </v-card-text>
          <v-card-actions class="pa-6 pt-0 d-flex justify-end">
            <v-btn text color="grey darken-1" @click="deleteDialog = false">
              Cancelar
            </v-btn>
            <v-btn
              color="error"
              class="px-6 rounded-lg"
              :loading="deleting"
              @click="executeDarDeBaja"
            >
              <v-icon left>mdi-account-remove</v-icon>
              Confirmar Baja
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar Notificaciones -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="4000" top right>
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            Cerrar
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </v-app>
</template>

<script>
import {
  getProfesionalesABM,
  createProfesionalABM,
  updateProfesionalABM,
  darDeBajaProfesional,
  getDataDr
} from '@/services/monolito';
import apiMonolito from '@/services/apiMonolito';

export default {
  name: 'ProfesionalABM',
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      searchLastname: '',
      includeInactive: true,
      profesionales: [],

      headers: [
        { text: 'DNI / Documento', value: 'dni', sortable: false },
        { text: 'Apellido', value: 'lastname', sortable: true },
        { text: 'Nombre', value: 'name', sortable: true },
        { text: 'Especialidad', value: 'especialidad', sortable: false },
        { text: 'Teléfono', value: 'telefono', sortable: false },
        { text: 'Matrícula', value: 'matricula', sortable: false },
        { text: 'Estado', value: 'active', sortable: true },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],

      createDialog: false,
      isEditing: false,
      editingId: null,
      formValid: false,
      newProf: {
        dni: '',
        name: '',
        lastname: '',
        gender: '',
        especialidad: null,
        telefono: '',
        matricula: ''
      },

      genderOptions: [
        { text: 'MASCULINO', value: 'masculino' },
        { text: 'FEMENINO', value: 'femenino' }
      ],
      especialidadOptions: [],

      deleteDialog: false,
      selectedProf: null,

      snackbar: {
        show: false,
        text: '',
        color: 'success'
      }
    };
  },
  created() {
    this.loadEspecialidades();
    this.loadProfesionales();
  },
  methods: {
    async loadProfesionales() {
      this.loading = true;
      try {
        const data = await getProfesionalesABM(this.searchLastname, this.includeInactive);
        this.profesionales = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Error cargando profesionales:', error);
        this.showToast('Error cargando la lista de profesionales', 'error');
      } finally {
        this.loading = false;
      }
    },

    async loadEspecialidades() {
      try {
        const data = await getDataDr();
        this.especialidadOptions = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('Error cargando especialidades:', error);
      }
    },

    onClearSearch() {
      this.searchLastname = '';
      this.loadProfesionales();
    },

    resetFilters() {
      this.searchLastname = '';
      this.includeInactive = true;
      this.loadProfesionales();
    },

    getDni(item) {
      if (item.party && item.party.ref) return item.party.ref;
      if (item.party && item.party.code) return item.party.code;
      return item.code || '-';
    },

    getApellido(item) {
      if (item.party && item.party.lastname) return item.party.lastname;
      return item.lastname || '-';
    },

    getNombre(item) {
      if (item.party && item.party.name) return item.party.name;
      return item.name || '-';
    },

    getEspecialidad(item) {
      if (item.specialty && item.specialty.name) return item.specialty.name;
      return '-';
    },

    openCreateDialog() {
      this.isEditing = false;
      this.editingId = null;
      this.newProf = {
        dni: '',
        name: '',
        lastname: '',
        gender: '',
        especialidad: null,
        telefono: '',
        matricula: ''
      };
      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation();
      }
      this.createDialog = true;
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.editingId = item.id;

      let specId = null;
      if (item.specialty) {
        specId = item.specialty.specialty || item.specialty.id || null;
      }

      let genderVal = 'masculino';
      if (item.party && item.party.gender) {
        genderVal = (item.party.gender === 'f' || item.party.gender === 'femenino') ? 'femenino' : 'masculino';
      }

      this.newProf = {
        dni: this.getDni(item) !== '-' ? this.getDni(item) : '',
        name: this.getNombre(item) !== '-' ? this.getNombre(item) : '',
        lastname: this.getApellido(item) !== '-' ? this.getApellido(item) : '',
        gender: genderVal,
        especialidad: specId,
        telefono: item.telefono || '',
        matricula: item.matricula || ''
      };

      if (this.$refs.createForm) {
        this.$refs.createForm.resetValidation();
      }
      this.createDialog = true;
    },

    closeCreateDialog() {
      this.createDialog = false;
      this.isEditing = false;
      this.editingId = null;
    },

    async saveNewProfesional() {
      if (!this.$refs.createForm.validate()) return;

      this.saving = true;
      try {
        const payload = {
          dni: this.newProf.dni,
          name: this.newProf.name,
          lastname: this.newProf.lastname,
          gender: this.newProf.gender,
          especialidad: this.newProf.especialidad,
          telefono: this.newProf.telefono,
          matricula: this.newProf.matricula,
          active: true
        };

        if (this.isEditing && this.editingId) {
          await updateProfesionalABM(this.editingId, payload);
          this.showToast('Profesional actualizado exitosamente', 'success');
        } else {
          await createProfesionalABM(payload);
          this.showToast('Profesional creado exitosamente', 'success');
        }

        this.closeCreateDialog();
        this.loadProfesionales();
      } catch (error) {
        console.error('Error guardando profesional:', error);
        this.showToast('Error al guardar profesional', 'error');
      } finally {
        this.saving = false;
      }
    },

    confirmDarDeBaja(item) {
      this.selectedProf = item;
      this.deleteDialog = true;
    },

    async executeDarDeBaja() {
      if (!this.selectedProf) return;

      this.deleting = true;
      try {
        await darDeBajaProfesional(this.selectedProf.id);
        this.showToast('Profesional dado de baja (active=false)', 'success');
        this.deleteDialog = false;
        this.selectedProf = null;
        this.loadProfesionales();
      } catch (error) {
        console.error('Error al dar de baja:', error);
        this.showToast('Error al dar de baja al profesional', 'error');
      } finally {
        this.deleting = false;
      }
    },

    async reactivar(item) {
      try {
        await apiMonolito.patch(`/profesional/${item.id}`, { active: true });
        this.showToast('Profesional reactivado correctamente', 'success');
        this.loadProfesionales();
      } catch (error) {
        console.error('Error reactivando profesional:', error);
        this.showToast('Error al reactivar el profesional', 'error');
      }
    },

    showToast(text, color = 'success') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    }
  }
};
</script>

<style scoped>
.app-background {
  background: #f5f7fa;
}
.opacity-80 {
  opacity: 0.8;
}
</style>
